import React, { useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  CONTACT_STATUSES,
  CONTACT_STATUS_LABELS,
} from "./contactStatuses";
import {
  listContactSubmissions,
  updateContactSubmissionStatus,
  countByStatus,
} from "./contactStore";
import "./mgmt.css";

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function ContactDashboard() {
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState(() => listContactSubmissions());

  const refresh = useCallback(() => {
    setRows(listContactSubmissions());
  }, []);

  const counts = useMemo(() => countByStatus(rows), [rows]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const onStatusChange = (id, status) => {
    updateContactSubmissionStatus(id, status);
    refresh();
  };

  return (
    <div className="mgmt-page">
      <header className="mgmt-topbar">
        <div className="container d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <h1>Contact submissions</h1>
            <p className="subtitle">
              Local list for now (saved in this browser). Wire your admin API
              here when the backend exposes list + patch endpoints.
            </p>
          </div>
          <Link className="mgmt-back" to="/">
            ← Back to site
          </Link>
        </div>
      </header>

      <div className="container mgmt-body">
        <div className="mgmt-filters" role="tablist" aria-label="Filter by status">
          <button
            type="button"
            className={`mgmt-filter-btn${filter === "all" ? " active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All <span className="count">({rows.length})</span>
          </button>
          {CONTACT_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`mgmt-filter-btn${filter === s ? " active" : ""}`}
              onClick={() => setFilter(s)}
            >
              {CONTACT_STATUS_LABELS[s]}{" "}
              <span className="count">({counts[s]})</span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="mgmt-table-wrap mgmt-empty">
            <p className="mb-2">
              <strong>No submissions</strong> in this view.
            </p>
            <p className="mb-0 small">
              Successful sends from the public Contact form are stored in
              <code className="mx-1">localStorage</code>
              for this browser. Submit a test message on the homepage to see
              rows here.
            </p>
          </div>
        ) : (
          <div className="mgmt-table-wrap table-responsive">
            <table className="table table-hover mgmt-table mb-0">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Message</th>
                  <th>Created</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <select
                        className="form-select form-select-sm mgmt-status-select"
                        aria-label={`Status for ${r.fullname || r.emailAddress}`}
                        value={r.status}
                        onChange={(e) =>
                          onStatusChange(r.id, e.target.value)
                        }
                      >
                        {CONTACT_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {CONTACT_STATUS_LABELS[s]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{r.fullname || "—"}</td>
                    <td>
                      {r.emailAddress ? (
                        <a href={`mailto:${r.emailAddress}`}>
                          {r.emailAddress}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {r.phoneNumber ? (
                        <a href={`tel:${r.phoneNumber}`}>{r.phoneNumber}</a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span className="msg-preview" title={r.message}>
                        {r.message || "—"}
                      </span>
                    </td>
                    <td className="text-muted small text-nowrap">
                      {formatWhen(r.createdAt)}
                    </td>
                    <td className="text-muted small text-nowrap">
                      {formatWhen(r.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
